import { Button, buttonVariants } from '@/components/Button';
import { Text, fontWeightEnum, textVariantsEnum } from '@/components/Text';
import { getRegularizationReporterLabel } from '@/views/Regulation/Regularization.helper';
import styles from './RegularizationRequests.module.scss';

const parseDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object') {
    const rawSeconds =
      value._seconds ?? value.seconds ?? value.sec ?? value.epochSeconds;
    const seconds = rawSeconds != null ? Number(rawSeconds) : null;
    if (seconds != null) {
      const nanos = Number(value._nanoseconds ?? value.nanoseconds ?? 0);
      if (!Number.isNaN(seconds) && !Number.isNaN(nanos)) {
        const parsed = new Date(seconds * 1000 + Math.floor(nanos / 1000000));
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
    }
  }
  if (typeof value === 'string') {
    const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatAsDdMmYyyy = (value) => {
  const date = parseDateValue(value);
  if (!date) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const columnsConfig = ({
  teams,
  memberDetails,
  toggle,
  setSelectedData,
}) => {
  return [
    {
      id: 'id',
      name: 'ID',
      sortable: false,
      cellRenderer: (row, rowIndex) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {rowIndex + 1}
          </Text>
        );
      },
    },
    {
      id: 'project',
      name: 'Project',
      sortable: false,
      cellRenderer: (row) => {
        const project = Array.isArray(teams?.projects)
          ? teams?.projects?.find((p) => p.id === row.projectId)
          : null;
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {project?.data?.project_name || 'N/A'}
          </Text>
        );
      },
    },
    {
      id: 'taskName',
      name: 'Task Name',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {row.taskName || 'N/A'}
          </Text>
        );
      },
    },
    {
      id: 'taskTime',
      name: 'Task Time',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >{`${row.taskHours}h ${row.taskMinutes}m`}</Text>
        );
      },
    },
    {
      id: 'taskDate',
      name: 'Task Date',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {formatAsDdMmYyyy(row?.startTime)}
          </Text>
        );
      },
    },
    {
      id: 'department',
      name: 'Department',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {row.teamName || 'N/A'}
          </Text>
        );
      },
    },
    {
      id: 'reportDate',
      name: 'Report Date',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {formatAsDdMmYyyy(row?.createdAt || row?.requestDate)}
          </Text>
        );
      },
    },
    {
      id: 'assignee',
      name: 'Assignee',
      sortable: false,
      cellRenderer: (row) => {
        const m = memberDetails?.[row.assignee];
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {m?.userName || m?.userEmailid || row.assignee || ''}
          </Text>
        );
      },
    },
    {
      id: 'reporter',
      name: 'Reporter',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            className={styles.cellText}
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {getRegularizationReporterLabel(row, memberDetails)}
          </Text>
        );
      },
    },
    {
      id: 'status',
      name: 'Status',
      sortable: false,
      cellRenderer: (row) => {
        return (
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
          >
            {row?.status}
          </Text>
        );
      },
    },
    {
      id: 'action',
      name: 'Action',
      sortable: false,
      cellRenderer: (row) => {
        if (row?.status === 'Approved') {
          return (
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Medium}
            >
              Completed
            </Text>
          );
        }
        if (row?.status === 'Declined') {
          return (
            <Text
              variant={textVariantsEnum.Text_sm}
              fontWeight={fontWeightEnum.Medium}
            >
              Declined
            </Text>
          );
        }
        return (
          <Button
            text="Process"
            variant={buttonVariants.primary_rounded}
            onClick={() => {
              toggle();
              setSelectedData(row);
            }}
          />
        );
      },
    },
  ];
};
